import { CandidateService } from "Services/candidateService";
import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Spinner, Tab, Table, Tabs } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import "./candidates.css";

const editableFields = [
    { key: "name", label: "First Name", col: 6 },
    { key: "last_name", label: "Last Name", col: 6 },
    { key: "dob", label: "Date of Birth", col: 6, type: "date" },
    { key: "gender", label: "Gender", col: 6, type: "select", options: ["male", "female", "other"] },
    { key: "mobile", label: "Mobile (WhatsApp)", col: 6 },
    { key: "city", label: "City", col: 6 },
    { key: "state", label: "State", col: 6 },
    { key: "registrationNumber", label: "Registration No.", col: 6 },
    { key: "centerId", label: "Center ID", col: 6 },
    { key: "username", label: "Username", col: 6 },
    { key: "qualification", label: "Qualification", col: 6 },
    { key: "membershipNumber", label: "Membership No.", col: 6 },
    { key: "isIriaMember", label: "IRIA Member", col: 6, type: "checkbox" },
];

export default function CandidateDetail() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [approving, setApproving] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState<any>({});
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState("details");
    const [exams, setExams] = useState<any[]>([]);
    const [examsLoading, setExamsLoading] = useState(false);
    const [transactions, setTransactions] = useState<any[]>([]);
    const [transactionsLoading, setTransactionsLoading] = useState(false);

    const fetchUser = async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const res = await CandidateService.getDetails(userId);
            if (res.status === 200) {
                setUser(res.data);
            }
        } catch (e) {
            console.error(e);
            toast.error("Failed to load candidate details");
        }
        setLoading(false);
    };

    const fetchExams = async () => {
        if (!userId) return;
        setExamsLoading(true);
        try {
            const res = await CandidateService.getExams(userId);
            if (res.status === 200) {
                setExams(res.data?.mappings || []);
            }
        } catch (e) {
            console.error(e);
        }
        setExamsLoading(false);
    };

    const fetchTransactions = async () => {
        if (!userId) return;
        setTransactionsLoading(true);
        try {
            const res = await CandidateService.getTransactions(userId);
            if (res.status === 200) {
                setTransactions(res.data?.transactions || []);
            }
        } catch (e) {
            console.error(e);
        }
        setTransactionsLoading(false);
    };

    useEffect(() => {
        fetchUser();
    }, [userId]);

    useEffect(() => {
        if (activeTab === "exams" && exams.length === 0) fetchExams();
        if (activeTab === "transactions" && transactions.length === 0) fetchTransactions();
    }, [activeTab]);

    const startEditing = () => {
        const form: any = {};
        for (const f of editableFields) {
            if (f.type === "date" && user[f.key]) {
                form[f.key] = new Date(user[f.key]).toISOString().split("T")[0];
            } else {
                form[f.key] = user[f.key] ?? "";
            }
        }
        setEditForm(form);
        setEditing(true);
    };

    const cancelEditing = () => {
        setEditing(false);
        setEditForm({});
    };

    const handleSave = async () => {
        if (!userId) return;
        setSaving(true);
        try {
            const payload: any = {};
            for (const f of editableFields) {
                const newVal = f.type === "checkbox" ? !!editForm[f.key] : editForm[f.key];
                if (newVal !== (user[f.key] ?? "")) {
                    payload[f.key] = newVal;
                }
            }
            if (Object.keys(payload).length === 0) {
                toast.info("No changes to save");
                setEditing(false);
                setSaving(false);
                return;
            }
            const res = await CandidateService.edit(userId, payload);
            if (res.status === 200) {
                toast.success("Candidate updated successfully");
                setEditing(false);
                fetchUser();
            }
        } catch (e) {
            toast.error("Failed to update candidate");
        }
        setSaving(false);
    };

    const handleApproval = async (approve: boolean) => {
        if (!userId) return;
        setApproving(true);
        try {
            const res = await CandidateService.approve(userId, approve);
            if (res.status === 200) {
                toast.success(approve ? "Candidate approved" : "Approval revoked");
                fetchUser();
            }
        } catch (e) {
            toast.error("Failed to update approval status");
        }
        setApproving(false);
    };

    if (loading) {
        return (
            <div className="text-center py-5 mt-4">
                <Spinner animation="border" size="sm" />
            </div>
        );
    }

    if (!user) {
        return (
            <div className="text-center py-5 mt-4 text-muted">Candidate not found</div>
        );
    }

    const isImage = (url: string) => {
        if (!url) return false;
        return /\.(jpg|jpeg|png|gif|webp|bmp)(\?.*)?$/i.test(url);
    };

    const renderField = (field: typeof editableFields[0]) => {
        if (!editing) {
            let display: any = user[field.key];
            if (field.type === "date" && display) display = new Date(display).toLocaleDateString("en-IN");
            else if (field.type === "checkbox") display = display ? <Badge bg="info">Yes</Badge> : <Badge bg="light" text="dark">No</Badge>;
            else if (field.key === "gender") display = <span style={{ textTransform: "capitalize" }}>{display || "-"}</span>;
            else display = display || "-";
            return (
                <Col xs={field.col} key={field.key}>
                    <div className="info-label">{field.label}</div>
                    <div className="info-value">{display}</div>
                </Col>
            );
        }

        if (field.type === "checkbox") {
            return (
                <Col xs={field.col} key={field.key}>
                    <Form.Check
                        label={field.label}
                        checked={!!editForm[field.key]}
                        onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.checked })}
                        className="mt-2"
                    />
                </Col>
            );
        }

        if (field.type === "select") {
            return (
                <Col xs={field.col} key={field.key}>
                    <Form.Group>
                        <Form.Label className="info-label mb-0">{field.label}</Form.Label>
                        <Form.Select
                            size="sm"
                            value={editForm[field.key] || ""}
                            onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.value })}
                        >
                            <option value="">-</option>
                            {field.options?.map((o) => (
                                <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
            );
        }

        return (
            <Col xs={field.col} key={field.key}>
                <Form.Group>
                    <Form.Label className="info-label mb-0">{field.label}</Form.Label>
                    <Form.Control
                        size="sm"
                        type={field.type || "text"}
                        value={editForm[field.key] || ""}
                        onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.value })}
                    />
                </Form.Group>
            </Col>
        );
    };

    return (
        <div className="mt-4 mb-5">
            <Button variant="link" className="text-decoration-none mb-3 ps-0" onClick={() => navigate(-1)}>
                &larr; Back to Candidates
            </Button>

            {/* Header */}
            <div className="detail-header mb-4">
                <div className="d-flex align-items-center gap-3">
                    {user.image ? (
                        <img src={user.image} alt="" className="detail-avatar" />
                    ) : (
                        <div className="detail-avatar-placeholder">
                            {(user.name || "?")[0].toUpperCase()}
                        </div>
                    )}
                    <div>
                        <h4 className="mb-1">{user.name} {user.last_name || ""}</h4>
                        <div className="opacity-75">{user.email}</div>
                        {user.registrationNumber && (
                            <small className="opacity-50">Reg. No: {user.registrationNumber}</small>
                        )}
                    </div>
                    <div className="ms-auto d-flex align-items-center gap-2">
                        {activeTab === "details" && !editing && (
                            <Button variant="outline-primary" size="sm" onClick={startEditing}>
                                Edit Details
                            </Button>
                        )}
                        {activeTab === "details" && editing && (
                            <>
                                <Button variant="success" size="sm" onClick={handleSave} disabled={saving}>
                                    {saving ? <Spinner animation="border" size="sm" /> : "Save"}
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={cancelEditing} disabled={saving}>
                                    Cancel
                                </Button>
                            </>
                        )}
                        {user.isApproved ? (
                            <Badge bg="success" className="px-3 py-2" style={{ fontSize: "0.9rem" }}>Approved</Badge>
                        ) : (
                            <Badge bg="warning" text="dark" className="px-3 py-2" style={{ fontSize: "0.9rem" }}>Pending Approval</Badge>
                        )}
                    </div>
                </div>
            </div>

            <Tabs activeKey={activeTab} onSelect={(k: any) => setActiveTab(k)} className="mb-3">
                {/* Tab 1: User Details */}
                <Tab eventKey="details" title="User Details">
                    <Row className="g-4 mt-1">
                        <Col md={6}>
                            <Card className="detail-card h-100">
                                <Card.Body className="p-4">
                                    <h6 className="mb-3 text-uppercase text-muted" style={{ letterSpacing: 1 }}>Personal Information</h6>
                                    <Row className="g-3">
                                        {editableFields.slice(0, 8).map(renderField)}
                                        <Col xs={6}>
                                            <div className="info-label">Email</div>
                                            <div className="info-value">{user.email || "-"}</div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="detail-card h-100">
                                <Card.Body className="p-4">
                                    <h6 className="mb-3 text-uppercase text-muted" style={{ letterSpacing: 1 }}>Professional Details</h6>
                                    <Row className="g-3">
                                        {editableFields.slice(8).map(renderField)}
                                        <Col xs={6}>
                                            <div className="info-label">Account Active</div>
                                            <div className="info-value">
                                                {user.isActive ? <Badge bg="success">Active</Badge> : <Badge bg="danger">Inactive</Badge>}
                                            </div>
                                        </Col>
                                        <Col xs={6}>
                                            <div className="info-label">Registered On</div>
                                            <div className="info-value">
                                                {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-IN') : "-"}
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="detail-card h-100">
                                <Card.Body className="p-4">
                                    <h6 className="mb-3 text-uppercase text-muted" style={{ letterSpacing: 1 }}>Qualification Document</h6>
                                    {user.qualificationDoc ? (
                                        <div>
                                            {isImage(user.qualificationDoc) ? (
                                                <div>
                                                    <img src={user.qualificationDoc} alt="Qualification Document" className="doc-preview mb-2" />
                                                    <br />
                                                </div>
                                            ) : null}
                                            <a href={user.qualificationDoc} target="_blank" rel="noreferrer" className="doc-link">
                                                View Document
                                            </a>
                                        </div>
                                    ) : (
                                        <div className="text-muted py-3">No document uploaded</div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="detail-card h-100">
                                <Card.Body className="p-4">
                                    <h6 className="mb-3 text-uppercase text-muted" style={{ letterSpacing: 1 }}>Approval</h6>
                                    <div className="approval-section">
                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                            <div>
                                                <div className="info-label">Current Status</div>
                                                <div className="info-value">
                                                    {user.isApproved ? (
                                                        <Badge bg="success" className="px-3 py-2">Approved</Badge>
                                                    ) : (
                                                        <Badge bg="warning" text="dark" className="px-3 py-2">Pending Approval</Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {user.isApproved && user.approvedBy && (
                                            <div className="mb-3">
                                                <Row className="g-2">
                                                    <Col xs={6}>
                                                        <div className="info-label">Approved By</div>
                                                        <div className="info-value">
                                                            {user.approvedBy.name || user.approvedBy.email || "-"}
                                                        </div>
                                                    </Col>
                                                    <Col xs={6}>
                                                        <div className="info-label">Approved At</div>
                                                        <div className="info-value">
                                                            {user.approvedAt ? new Date(user.approvedAt).toLocaleString('en-IN') : "-"}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        )}
                                        <hr />
                                        <div className="d-flex gap-2">
                                            {!user.isApproved ? (
                                                <Button variant="success" onClick={() => handleApproval(true)} disabled={approving}>
                                                    {approving ? <Spinner animation="border" size="sm" /> : "Approve Candidate"}
                                                </Button>
                                            ) : (
                                                <Button variant="outline-danger" onClick={() => handleApproval(false)} disabled={approving}>
                                                    {approving ? <Spinner animation="border" size="sm" /> : "Revoke Approval"}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Tab>

                {/* Tab 2: Enrolled Exams */}
                <Tab eventKey="exams" title={`Enrolled Exams${exams.length ? ` (${exams.length})` : ''}`}>
                    <Card className="border-0 shadow-sm mt-1">
                        <Card.Body className="p-0">
                            {examsLoading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Loading exams...
                                </div>
                            ) : exams.length === 0 ? (
                                <div className="text-center py-5 text-muted fs-12">No enrolled exams found</div>
                            ) : (
                                <Table hover responsive className="mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 50 }}>#</th>
                                            <th>Exam Name</th>
                                            <th>Subject</th>
                                            <th>Status</th>
                                            <th>Questions</th>
                                            <th>Answered</th>
                                            <th>Approved</th>
                                            <th>Rejected</th>
                                            <th>Fee Paid</th>
                                            <th>Completed</th>
                                            <th>Active</th>
                                            <th>Enrolled On</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {exams.map((m: any, i: number) => (
                                            <tr key={m._id}>
                                                <td className="text-muted">{i + 1}</td>
                                                <td className="fw-semibold">{m.exam?.examName || "-"}</td>
                                                <td className="text-muted">{m.exam?.subject || "-"}</td>
                                                <td>
                                                    <Badge bg={
                                                        m.exam?.status === "live" ? "danger"
                                                        : m.exam?.status === "completed" ? "success"
                                                        : "info"
                                                    }>
                                                        {m.exam?.status || "-"}
                                                    </Badge>
                                                </td>
                                                <td className="text-center">{m.totalQuestions ?? 0}</td>
                                                <td className="text-center">{m.answeredCount ?? 0}</td>
                                                <td className="text-center">{m.approvedCount ?? 0}</td>
                                                <td className="text-center">{m.rejectedCount ?? 0}</td>
                                                <td>
                                                    {m.isFeePaid
                                                        ? <Badge bg="success">Paid</Badge>
                                                        : <Badge bg="warning" text="dark">Pending</Badge>}
                                                </td>
                                                <td>
                                                    {m.completionStatus
                                                        ? <Badge bg="success">Yes</Badge>
                                                        : <Badge bg="light" text="dark">No</Badge>}
                                                </td>
                                                <td>
                                                    {m.active
                                                        ? <Badge bg="success">Yes</Badge>
                                                        : <Badge bg="secondary">No</Badge>}
                                                </td>
                                                <td className="text-muted fs-12">
                                                    {m.createdAt ? moment(m.createdAt).format("DD-MMM-YYYY hh:mm a") : "-"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>

                {/* Tab 3: Transactions */}
                <Tab eventKey="transactions" title={`Transactions${transactions.length ? ` (${transactions.length})` : ''}`}>
                    <Card className="border-0 shadow-sm mt-1">
                        <Card.Body className="p-0">
                            {transactionsLoading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Loading transactions...
                                </div>
                            ) : transactions.length === 0 ? (
                                <div className="text-center py-5 text-muted fs-12">No transactions found</div>
                            ) : (
                                <Table hover responsive className="mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 50 }}>#</th>
                                            <th>Order ID</th>
                                            <th>Type</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                            <th>Payment ID</th>
                                            <th>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transactions.map((txn: any, i: number) => (
                                            <tr key={txn._id}>
                                                <td className="text-muted">{i + 1}</td>
                                                <td className="fs-12" style={{ fontFamily: "monospace" }}>
                                                    {txn.orderId || "-"}
                                                </td>
                                                <td>
                                                    {txn.examGroup
                                                        ? <><Badge bg="primary" className="me-1">Group</Badge>{txn.examGroup.name || ""}</>
                                                        : <><Badge bg="secondary" className="me-1">Exam</Badge>{txn.exam?.examName || ""}</>}
                                                </td>
                                                <td className="fw-semibold">
                                                    {txn.currency === "INR" ? "₹" : "$"}{txn.amount?.toLocaleString("en-IN") || "0"}
                                                </td>
                                                <td>
                                                    <Badge bg={
                                                        txn.status === "completed" ? "success"
                                                        : txn.status === "pending" ? "warning"
                                                        : txn.status === "refunded" ? "info"
                                                        : "secondary"
                                                    } text={txn.status === "pending" ? "dark" : undefined}>
                                                        {txn.status || "-"}
                                                    </Badge>
                                                </td>
                                                <td className="text-muted fs-12" style={{ fontFamily: "monospace" }}>
                                                    {txn.paymentId || "-"}
                                                </td>
                                                <td className="text-muted fs-12">
                                                    {txn.createdAt ? moment(txn.createdAt).format("DD-MMM-YYYY hh:mm a") : "-"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>
            </Tabs>
        </div>
    );
}
